import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';

import { StateCheckbox } from './StateCheckbox';
import { computeCheckboxListItemState } from './utils/checkbox-list-state.util';
import type { CheckboxListItem } from './types/checkbox-list-item.type';

const INDENT_SPACING_PER_DEPTH = 4;

interface ItemsProps<I extends string> {
  readonly childrenRequireParent: boolean;
  readonly depth: number;
  readonly idPrefix: string;
  readonly isEditable: boolean;
  readonly isParentChecked: boolean;
  readonly items: readonly CheckboxListItem<I>[];
  readonly onToggle: (items: readonly CheckboxListItem<I>[]) => void;
  readonly value: readonly I[];
}

export const CheckboxListItems = <I extends string>({
  childrenRequireParent,
  depth,
  idPrefix,
  isEditable,
  isParentChecked,
  items,
  onToggle,
  value,
}: Readonly<ItemsProps<I>>) =>
  items.map((item) => {
    const isChecked = value.includes(item.id);
    const isDisabled =
      !isEditable ||
      !!item.disabled ||
      (childrenRequireParent && !isParentChecked);

    return (
      <Stack key={item.id}>
        <FormControlLabel
          control={
            <StateCheckbox
              disabled={isDisabled}
              id={`${idPrefix}${item.id}`}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={() => {
                onToggle([item]);
              }}
              state={computeCheckboxListItemState(item, value)}
            />
          }
          label={item.label}
          sx={{ pl: depth * INDENT_SPACING_PER_DEPTH }}
        />
        {!!item.children?.length && (
          <CheckboxListItems
            childrenRequireParent={childrenRequireParent}
            depth={depth + 1}
            idPrefix={idPrefix}
            isEditable={isEditable}
            isParentChecked={isChecked}
            items={item.children}
            onToggle={onToggle}
            value={value}
          />
        )}
      </Stack>
    );
  });
